import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useBusinessLine } from '../contexts/BusinessLineContext';
import { useNotifications } from '../contexts/NotificationContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '../components/ui/hover-card';
import { toast } from 'sonner';
import { formatDate } from '../lib/utils';
import {
  Plus,
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Clock,
  User,
  Target,
  Building2,
  MoreVertical,
  Edit,
  Trash2,
  Video,
  X,
  UserPlus,
  Mail,
  Copy,
  ExternalLink,
  Users,
  RefreshCw,
  MapPin,
  Check,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const toLocalDateString = (dateStr) => {
  const d = new Date(dateStr);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
};

const getDaysInMonth = (year, month) => {
  const date = new Date(year, month, 1);
  const days = [];
  const firstDay = date.getDay();
  const prevMonth = new Date(year, month, 0);
  for (let i = firstDay - 1; i >= 0; i--) {
    days.push({ date: new Date(year, month - 1, prevMonth.getDate() - i), isCurrentMonth: false });
  }
  while (date.getMonth() === month) {
    days.push({ date: new Date(date), isCurrentMonth: true });
    date.setDate(date.getDate() + 1);
  }
  let nextDay = 1;
  while (days.length % 7 !== 0) {
    days.push({ date: new Date(year, month + 1, nextDay++), isCurrentMonth: false });
  }
  return days;
};

const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const WEEKDAYS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

const appointmentTypes = {
  client: { label: 'Cliente', color: 'bg-blue-500',   icon: User },
  lead:   { label: 'Lead',    color: 'bg-green-500',  icon: Target },
  agent:  { label: 'Agente',  color: 'bg-purple-500', icon: Building2 },
  other:  { label: 'Otro',    color: 'bg-gray-500',   icon: CalendarIcon },
};

const JUNTA_DURATION_OPTIONS = [
  { value: '30min', label: '30 minutos', mins: 30  },
  { value: '1hr',   label: '1 hora',     mins: 60  },
  { value: '1.5hr', label: '1.5 horas',  mins: 90  },
  { value: '2hr',   label: '2 horas',    mins: 120 },
  { value: '3hr',   label: '3 horas',    mins: 180 },
  { value: '4hr',   label: '4 horas',    mins: 240 },
];

const RECURRENCE_OPTIONS = [
  { value: 'none',     label: 'Ninguna (solo esta vez)'     },
  { value: 'daily',    label: 'Diaria'                      },
  { value: 'weekly',   label: 'Semanal'                     },
  { value: 'monthly',  label: 'Mensual'                     },
  { value: 'weekdays', label: 'Días entre semana (lun-vie)' },
];

const COLOR_LEGEND = [
  { key: 'birthday',          color: '#f59e0b', label: 'Cumpleaños'         },
  { key: 'renewal',           color: '#dc2626', label: 'Renovación'          },
  { key: 'policy_start',      color: '#10b981', label: 'Inicio de póliza'   },
  { key: 'anniversary',       color: '#F5CF27', label: 'Aniversario póliza' },
  { key: 'junta',             color: '#8b5cf6', label: 'Junta grupal'       },
  { key: 'cita',              color: '#3b82f6', label: 'Citas'              },
];

const USER_COLORS = ['#0ea5e9','#f97316','#10b981','#a855f7','#ec4899','#14b8a6','#eab308','#f43f5e'];
const getUserColor = (userId) => {
  if (!userId) return '#6b7280';
  let h = 0;
  for (let i = 0; i < userId.length; i++) h = (h * 31 + userId.charCodeAt(i)) >>> 0;
  return USER_COLORS[h % USER_COLORS.length];
};

const ROLE_LABELS = {
  admin: 'Admin', master: 'Master', agente: 'Agente',
  asesor: 'Asesor', developer: 'Developer', asistente: 'Asistente',
  aspirante: 'Aspirante', viewer: 'Viewer',
};
const getRoleLabel = (role) => ROLE_LABELS[role] || role || '';
const getUserLabel = (apt) => {
  if (!apt.user_name || !apt.user_role) return null;
  return `${getRoleLabel(apt.user_role)} ${apt.user_name}`;
};

const getTextColor = (bgColor) => {
  if (!bgColor) return '#ffffff';
  const hex = bgColor.replace('#', '');
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#1a1a1a' : '#ffffff';
};

const START_HOUR = 0;
const END_HOUR = 24;
const HOUR_HEIGHT = 64;

const getEventTop = (datetime) => {
  const d = new Date(datetime);
  const hours = d.getHours() + d.getMinutes() / 60;
  return Math.max(0, (hours - START_HOUR) * HOUR_HEIGHT);
};

const getEventHeight = (start, end) => {
  if (!end) return HOUR_HEIGHT;
  const s = new Date(start);
  const e = new Date(end);
  const diff = (e - s) / 3600000;
  return Math.max(30, diff * HOUR_HEIGHT);
};

// 48 opciones de hora: cada 30 min, valor HH:MM (24h), etiqueta en 12h AM/PM
const TIME_OPTIONS = Array.from({ length: 48 }, (_, i) => {
  const h  = Math.floor(i / 2);
  const m  = (i % 2) * 30;
  const hh = String(h).padStart(2, '0');
  const mm = String(m).padStart(2, '0');
  const period = h < 12 ? 'AM' : 'PM';
  const h12    = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return { value: `${hh}:${mm}`, label: `${h12}:${mm} ${period}` };
});

const TIME_OPTIONS_FULL = (() => {
  const opts = [];
  for (let h = 0; h < 24; h++) {
    for (const m of [0, 30]) {
      const hh = String(h).padStart(2, '0');
      const mm = String(m).padStart(2, '0');
      const time24 = `${hh}:${mm}`;
      const period = h < 12 ? 'AM' : 'PM';
      const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
      const time12 = `${h12}:${mm} ${period}`;
      opts.push({ value: time24, label24: time24, label12: time12 });
    }
  }
  return opts;
})();

const getNextHalfHour = () => {
  const now = new Date();
  const mins = now.getMinutes();
  const nextMins = mins < 30 ? 30 : 0;
  const nextHour = mins < 30 ? now.getHours() : now.getHours() + 1;
  return `${String(nextHour % 24).padStart(2, '0')}:${String(nextMins).padStart(2, '0')}`;
};

const addOneHour = (timeStr) => {
  const [h, m] = (timeStr || '09:00').split(':').map(Number);
  return `${String((h + 1) % 24).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

// Mapeo id de línea (BusinessLineContext) → valor exacto guardado en user.aseguradoras[]
const LINE_ID_TO_ASEGURADORA = {
  allianz: 'Allianz',
  insignia: 'Insignia Life',
  qualitas: 'Quálitas',
};

export const CalendarPage = () => {
  const { user, canCreate } = useAuth();
  const { availableLines } = useBusinessLine();
  const { refresh: refreshNotifications } = useNotifications();
  const isAdmin = user?.role === 'admin';
  const isAsistente = user?.role === 'asistente';
  const canCreateJunta = user?.role === 'admin' || user?.role === 'asistente' || user?.role === 'developer';
  const today = new Date();

  const [currentDate, setCurrentDate]   = useState(new Date(today));
  const [viewMode, setViewMode]         = useState('week');
  const [appointments, setAppointments] = useState([]);

  // Cita dialog
  const [dialogOpen, setDialogOpen]               = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [selectedDate, setSelectedDate]           = useState(null);

  // View dialog
  const [viewingAppointment, setViewingAppointment] = useState(null);
  const [viewDialogOpen, setViewDialogOpen]         = useState(false);
  const [juntaParticipantNames, setJuntaParticipantNames] = useState([]);

  // Junta dialog
  const [juntaDialogOpen, setJuntaDialogOpen] = useState(false);
  const [editingJunta, setEditingJunta]       = useState(null);
  const [juntaFormData, setJuntaFormData]     = useState({
    title: '', date: '', time: '10:00', duration: '1hr',
    sede: '', participants: [], recurrence: 'none', repeatUntil: '', notes: '', videoLink: '', color: '',
  });

  // Filters
  const [filterSede, setFilterSede]   = useState('all');
  const [filterAgent, setFilterAgent] = useState(user?.id || 'all');
  const [visibleTypes, setVisibleTypes] = useState({
    birthday: true, renewal: true, policy_start: true,
    anniversary: true, junta: true, cita: true,
  });

  // Delete recurrence dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget]         = useState(null);

  // Google Calendar
  const [gcalConnected, setGcalConnected] = useState(false);
  const [gcalSyncing, setGcalSyncing]     = useState(false);
  const [gcalEvents, setGcalEvents]       = useState([]);

  // Users for junta participants
  const [availableUsers, setAvailableUsers] = useState([]);
  const [juntaSearch, setJuntaSearch] = useState('');
  const [meetingStaff, setMeetingStaff] = useState([]);

  // Multi-calendar for asistente
  const [calendarUsers, setCalendarUsers] = useState([]);
  const [activeUsers, setActiveUsers] = useState([]);
  const [forUserId, setForUserId] = useState('');

  // Tooltip
  const [hoveredEvent, setHoveredEvent]       = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  // Scroll ref for week timeline
  const weekScrollRef = useRef(null);

  // Form state for cita
  const [formData, setFormData] = useState(() => {
    const t = getNextHalfHour();
    return { title: '', date: '', time: t, endTime: addOneHour(t), type: 'client', cita_subtype: '', color: '', guests: [], videoLink: '', description: '', repeatUntil: '' };
  });
  const [calendarPrefs, setCalendarPrefs] = useState({ color_cita: '#3b82f6', color_junta: '#8b5cf6' });
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [newGuest, setNewGuest] = useState({ name: '', email: '' });
  const [startTimeMode, setStartTimeMode] = useState('select');
  const [endTimeMode,   setEndTimeMode]   = useState('select');

  const API = `${process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:8002'}/api`;
  const token = localStorage.getItem('token');
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const normalizeEvent = (e) => ({
    ...e,
    date:       e.start_datetime,
    endTime:    e.end_datetime || '',
    allDay:     e.all_day     || false,
    type:       e.event_type  || 'other',
    kind:       e.event_kind  || 'cita',
    videoLink:  e.video_link  || '',
    isReadonly: e.is_readonly || false,
    color:      e.color       || null,
    source:     e.source      || null,
  });

  // Callable loader — used by all create/edit/delete handlers
  const loadEvents = useCallback(async () => {
    const year     = currentDate.getFullYear();
    const month    = currentDate.getMonth();
    const startStr = new Date(year, month, 1).toISOString().split('T')[0];
    const endStr   = new Date(year, month + 1, 0).toISOString().split('T')[0];
    try {
      const [eventsRes, annivRes] = await Promise.all([
        axios.get(`${API}/calendar/events?start=${startStr}&end=${endStr}`, authHeaders),
        axios.get(`${API}/calendar/anniversaries?start_date=${startStr}&end_date=${endStr}`, authHeaders),
      ]);
      const annivEvents = (annivRes.data || []).map(a => {
        let agentName = null;
        let agentRole = null;
        if (a.agent_id) {
          if (a.agent_id === user?.id) {
            agentName = user?.full_name || user?.name || user?.email;
            agentRole = user?.role;
          } else {
            const found = availableUsers.find(u => u.id === a.agent_id);
            if (found) {
              agentName = found.full_name || found.name || found.email;
              agentRole = found.role;
            }
          }
        }
        return {
          id: a.id,
          title: `🎂 ${a.years}° Aniv. — ${a.client_name}`,
          date: `${a.anniversary_date}T09:00:00`,
          endTime: `${a.anniversary_date}T10:00:00`,
          type: 'policy_anniversary',
          source: 'anniversary',
          allDay: true,
          kind: 'cita',
          user_id: a.agent_id || null,
          user_name: agentName,
          user_role: agentRole,
          policyNumber: a.policy_number,
          clientName: a.client_name,
          years: a.years,
          status: a.status,
          isReadonly: true,
          color: '#F5CF27',
        };
      });
      const currentYear = new Date().getFullYear();
      const filteredEvents = eventsRes.data.filter(e => {
        if (e.source === 'policy_start') {
          return new Date(e.start_datetime).getFullYear() >= currentYear;
        }
        return true;
      });
      setAppointments([...filteredEvents.map(normalizeEvent), ...annivEvents]);
    } catch (err) {
      console.error('Error cargando citas:', err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate, availableUsers]);

  // Sync Google Calendar events
  const syncGoogleCalendar = async () => {
    setGcalSyncing(true);
    try {
      const res = await axios.get(`${API}/integrations/google-calendar/sync`, authHeaders);
      const events = (res.data.events || []).map(e => normalizeEvent({
        ...e,
        color:       '#ea4335',
        is_readonly: true,
        source:      'google',
      }));
      setGcalEvents(events);
      if (events.length > 0) toast.success(`${events.length} eventos de Google sincronizados`);
    } catch {
      toast.error('Error al sincronizar Google Calendar');
    } finally {
      setGcalSyncing(false);
    }
  };

  // Reload events whenever the visible month changes
  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  // Check Google Calendar connection on mount (disabled)
  useEffect(() => {
    if (false) axios.get(`${API}/integrations/google-calendar/status`, authHeaders)
      .then(res => {
        if (res.data.connected) {
          setGcalConnected(true);
          syncGoogleCalendar();
        }
      })
      .catch(() => {}); // not connected — silently skip
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load calendar color preferences on mount
  useEffect(() => {
    axios.get(`${API}/calendar/prefs`, authHeaders)
      .then(res => {
        if (res.data.color_cita) setCalendarPrefs(prev => ({ ...prev, color_cita: res.data.color_cita }));
        if (res.data.color_junta) setCalendarPrefs(prev => ({ ...prev, color_junta: res.data.color_junta }));
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch users for junta participants (admin + asistente + developer) — sorted alphabetically.
  // Carga usuarios y staff en paralelo; resuelve IDs de staff ausentes via /users/resolve
  // para que el botón "Seleccionar Staff" siempre tenga acceso a todos sus miembros.
  useEffect(() => {
    if (!isAdmin && user?.role !== 'asistente' && user?.role !== 'developer') return;
    (async () => {
      try {
        const [usersRes, staffRes] = await Promise.all([
          axios.get(`${API}/users?scope=junta`, authHeaders).catch(() => ({ data: [] })),
          axios.get(`${API}/settings/meeting-staff`, authHeaders).catch(() => ({ data: { meeting_staff: [] } })),
        ]);
        const users = Array.isArray(usersRes.data) ? usersRes.data : (usersRes.data.items || []);
        const staffIds = staffRes.data.meeting_staff || [];
        setMeetingStaff(staffIds);
        // Resolver miembros del staff que no estén ya en la lista de usuarios
        const knownIds = new Set(users.map(u => u.id));
        const missingIds = staffIds.filter(id => !knownIds.has(id));
        if (missingIds.length > 0) {
          const resolveRes = await axios.get(
            `${API}/users/resolve?ids=${missingIds.join(',')}`, authHeaders
          ).catch(() => ({ data: [] }));
          users.push(...(resolveRes.data || []));
        }
        users.sort((a, b) =>
          (a.full_name || a.name || a.email || '').localeCompare(b.full_name || b.name || b.email || '', 'es', { sensitivity: 'base' })
        );
        setAvailableUsers(users);
      } catch {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  // Fetch managed-agents list for asistente (multi-calendar toggles + "Crear para")
  useEffect(() => {
    if (!isAsistente) return;
    const managedIds = user?.managed_agents || [];
    const self = { id: user.id, name: 'Tú', color: getUserColor(user.id) };
    if (managedIds.length === 0) {
      setCalendarUsers([self]);
      setActiveUsers([user.id]);
      return;
    }
    axios.get(`${API}/users`, authHeaders)
      .then(res => {
        const all = Array.isArray(res.data) ? res.data : (res.data.items || []);
        const cu = [
          self,
          ...all
            .filter(u => managedIds.includes(u.id))
            .map(u => ({ id: u.id, name: u.full_name || u.name || u.email, color: getUserColor(u.id) })),
        ];
        setCalendarUsers(cu);
        setActiveUsers([user.id]);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAsistente]);

  const days = getDaysInMonth(currentDate.getFullYear(), currentDate.getMonth());

  // Aseguradoras disponibles para el usuario (excluye 'Todas las líneas' y 'Flipping', que no aplican a participantes de junta)
  const juntaAseguradoraLines = useMemo(
    () => availableLines.filter(l => l.id !== 'all' && l.id !== 'flipping'),
    [availableLines]
  );

  // Participantes de una aseguradora dada: admins/masters siempre incluidos + usuarios cuya aseguradoras[] la contenga
  const usersForAseguradora = useCallback((aseguradoraName) => {
    return availableUsers.filter(u => {
      if (u.id === user?.id) return false;
      if (u.role === 'admin' || u.role === 'master') return true;
      return (u.aseguradoras || []).includes(aseguradoraName);
    }).map(u => u.id);
  }, [availableUsers, user?.id]);

  const navigate = (direction) => {
    if (viewMode === 'week') {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate() + direction * 7));
    } else {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + direction, 1));
    }
  };

  const addGuest = () => {
    if (!newGuest.name && !newGuest.email) { toast.error('Ingresa al menos el nombre o el correo del invitado'); return; }
    setFormData(prev => ({ ...prev, guests: [...prev.guests, { ...newGuest }] }));
    setNewGuest({ name: '', email: '' });
  };

  const removeGuest = (index) => {
    setFormData(prev => ({ ...prev, guests: prev.guests.filter((_, i) => i !== index) }));
  };

  // Available sedes from real appointments
  const availableSedes = [...new Set(appointments.filter(a => a.sede).map(a => a.sede))];

  const getEventTypeKey = (apt) => {
    if (apt.source === 'birthday' || apt.type?.includes('birthday')) return 'birthday';
    if (apt.source === 'renewal') return 'renewal';
    if (apt.source === 'policy_start') return 'policy_start';
    if (apt.source === 'anniversary') return 'anniversary';
    if (apt.source === 'google') return 'google';
    if (apt.kind === 'junta') return 'junta';
    return 'cita';
  };

  const getAppointmentsForDate = (date) => {
    return [...appointments, ...gcalEvents].filter(apt => {
      const d = new Date(apt.date);
      const match = d.getDate() === date.getDate() && d.getMonth() === date.getMonth() && d.getFullYear() === date.getFullYear();
      if (!match) return false;
      if (!visibleTypes[getEventTypeKey(apt)]) return false;
      if (apt.isReadonly) return true; // synthetic / Google events bypass sede/agent filters
      if (filterSede !== 'all' && apt.sede !== filterSede) return false;
      if (isAsistente) {
        if (activeUsers.length > 0 && apt.user_id && !activeUsers.includes(apt.user_id)) {
          const isParticipant = (apt.participants || []).includes(user?.id);
          if (!isParticipant) return false;
        }
      } else if (user?.role === 'developer' || user?.role === 'master') {
        // backend ya acota el scope; no aplicar filtro adicional de agente en el frontend
      } else {
        if (filterAgent !== 'all' && apt.user_id !== filterAgent) {
          const isParticipant = (apt.participants || []).includes(filterAgent);
          if (!isParticipant) return false;
        }
      }
      return true;
    });
  };

  const getEventLabel = (apt) => {
    if (apt.source === 'birthday')     return 'Cumpleaños';
    if (apt.source === 'renewal')      return 'Renovación';
    if (apt.source === 'policy_start') return 'Inicio de póliza';
    if (apt.source === 'anniversary')  return 'Aniversario';
    if (apt.kind === 'junta')          return 'Junta';
    if (apt.kind === 'cita')           return 'Cita';
    return (appointmentTypes[apt.type] || appointmentTypes.other).label;
  };

  // Returns inline style + className for an event pill
  const getEventStyle = (apt) => {
    if (apt.color) return {
      bgStyle: { backgroundColor: apt.color, ...(apt.source === 'anniversary' ? { color: '#1a1a1a' } : {}) },
      bgClass: '',
    };
    if (apt.kind === 'junta') return { bgStyle: { backgroundColor: calendarPrefs.color_junta }, bgClass: '' };
    if (isAsistente && apt.user_id && apt.user_id !== user?.id)
      return { bgStyle: { backgroundColor: getUserColor(apt.user_id) }, bgClass: '' };
    if (apt.kind === 'cita') return { bgStyle: { backgroundColor: calendarPrefs.color_cita }, bgClass: '' };
    return { bgStyle: {}, bgClass: (appointmentTypes[apt.type] || appointmentTypes.other).color };
  };

  const saveCalendarPrefs = async () => {
    setSavingPrefs(true);
    try {
      await axios.put(`${API}/calendar/prefs`, calendarPrefs, authHeaders);
      toast.success('Preferencias de colores guardadas');
      setPrefsOpen(false);
    } catch {
      toast.error('Error al guardar preferencias');
    } finally {
      setSavingPrefs(false);
    }
  };

  const handleDateClick = (date) => {
    setSelectedDate(date);
    setFormData(prev => ({ ...prev, date: date.toISOString().split('T')[0] }));
    setDialogOpen(true);
  };

  const resolveParticipants = async (apt) => {
    if (!apt.user_id) { setJuntaParticipantNames([]); return; }
    const allIds = [...new Set([apt.user_id, ...(apt.participants || [])])];
    const toEntry = (users) => allIds
      .map(uid => { const u = users.find(u => u.id === uid); return u ? { id: uid, name: u.full_name || u.name || u.email, isHost: uid === apt.user_id } : null; })
      .filter(Boolean)
      .sort((a, b) => (b.isHost ? 1 : 0) - (a.isHost ? 1 : 0) || a.name.localeCompare(b.name, 'es'));
    if (availableUsers.length > 0) { setJuntaParticipantNames(toEntry(availableUsers)); return; }
    try {
      const res = await axios.get(`${API}/users/resolve`, {
        params: { ids: allIds.join(',') },
        headers: { Authorization: `Bearer ${token}` },
      });
      setJuntaParticipantNames(toEntry(res.data || []));
    } catch { setJuntaParticipantNames([]); }
  };

  const handleViewAppointment = (apt) => {
    setHoveredEvent(null);
    setViewingAppointment(apt);
    setViewDialogOpen(true);
    if (apt.kind === 'junta') resolveParticipants(apt);
  };

  const copyVideoLink = (link, e) => {
    if (e) e.stopPropagation();
    if (!link) { toast.error('No hay link de videollamada'); return; }
    navigator.clipboard.writeText(link);
    toast.success('Link copiado al portapapeles');
  };

  // Cita submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    const recurrence = formData.recurrence !== 'none' ? formData.recurrence : null;
    if (recurrence && !formData.repeatUntil) {
      toast.error('Indica la fecha "Repetir hasta" para la recurrencia');
      return;
    }
    const dateTime    = new Date(`${formData.date}T${formData.time}`);
    const endDatetime = formData.endTime
      ? new Date(`${formData.date}T${formData.endTime}`).toISOString()
      : null;
    const payload = {
      title:          formData.title,
      start_datetime: dateTime.toISOString(),
      end_datetime:   endDatetime,
      type:           formData.type,
      cita_subtype:   formData.cita_subtype || null,
      color:          formData.color || null,
      guests:         formData.guests,
      videoLink:      formData.videoLink,
      description:    formData.description,
      recurrence,
      repeat_until:   recurrence ? formData.repeatUntil : undefined,
      ...(isAsistente && forUserId && forUserId !== user?.id ? { for_user_id: forUserId } : {}),
    };
    try {
      if (editingAppointment) {
        await axios.put(`${API}/calendar/events/${editingAppointment.id}`, payload, authHeaders);
        toast.success('Cita actualizada correctamente');
      } else {
        await axios.post(`${API}/calendar/events`, payload, authHeaders);
        toast.success(recurrence ? 'Citas creadas con recurrencia' : 'Cita creada correctamente');
      }
      loadEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar la cita');
      console.error(err);
      return;
    }
    setDialogOpen(false);
    resetForm();
  };

  const handleEdit = (appointment) => {
    if (appointment.isReadonly) { handleViewAppointment(appointment); return; }
    if (appointment.kind === 'junta') { handleEditJunta(appointment); return; }
    setHoveredEvent(null);
    setEditingAppointment(appointment);
    const d = new Date(appointment.date);
    setFormData({
      title:        appointment.title,
      date:         toLocalDateString(appointment.date),
      time:         d.toTimeString().slice(0, 5),
      endTime:      appointment.endTime
                      ? new Date(appointment.endTime).toTimeString().slice(0, 5)
                      : '10:00',
      type:         appointment.type,
      cita_subtype: appointment.cita_subtype || '',
      color:        appointment.color || '',
      guests:       appointment.guests || [],
      videoLink:    appointment.videoLink || '',
      description:  appointment.description || appointment.notes || '',
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    const apt = [...appointments, ...gcalEvents].find(a => a.id === id);
    if (apt?.isReadonly) { toast.error('Este evento no puede eliminarse'); return; }
    if (apt?.kind === 'junta' && !(isAdmin || user?.role === 'master') && apt?.user_id !== user?.id) { toast.error('No tienes permiso para eliminar esta junta'); return; }
    if (apt?.recurrence_group_id) {
      setDeleteTarget(apt);
      setDeleteDialogOpen(true);
      return;
    }
    if (!window.confirm('¿Estás seguro de eliminar esta cita?')) return;
    try {
      await axios.delete(`${API}/calendar/events/${id}`, authHeaders);
      setJuntaDialogOpen(false);
      setViewDialogOpen(false);
      loadEvents();
      toast.success('Cita eliminada');
    } catch (err) {
      toast.error('Error al eliminar la cita');
    }
  };

  const handleDeleteSingle = async () => {
    setDeleteDialogOpen(false);
    try {
      await axios.delete(`${API}/calendar/events/${deleteTarget.id}`, authHeaders);
      setJuntaDialogOpen(false);
      setViewDialogOpen(false);
      loadEvents();
      toast.success('Evento eliminado');
    } catch {
      toast.error('Error al eliminar el evento');
    }
    setDeleteTarget(null);
  };

  const handleDeleteSeries = async () => {
    setDeleteDialogOpen(false);
    try {
      const res = await axios.delete(`${API}/calendar/events/group/${deleteTarget.recurrence_group_id}`, authHeaders);
      setJuntaDialogOpen(false);
      setViewDialogOpen(false);
      loadEvents();
      toast.success(`Se eliminaron ${res.data.deleted} eventos de la serie`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar la serie');
    }
    setDeleteTarget(null);
  };

  const resetForm = () => {
    setEditingAppointment(null);
    setSelectedDate(null);
    setNewGuest({ name: '', email: '' });
    setStartTimeMode('select');
    setEndTimeMode('select');
    setForUserId('');
    const t = getNextHalfHour();
    setFormData({ title: '', date: '', time: t, endTime: addOneHour(t), type: 'client', cita_subtype: '', color: '', guests: [], videoLink: '', description: '', recurrence: 'none', repeatUntil: '' });
  };

  // Junta handlers
  const handleEditJunta = (apt) => {
    if (!canCreateJunta) { handleViewAppointment(apt); return; }
    setHoveredEvent(null);
    setEditingJunta(apt);
    const d = new Date(apt.date);
    setJuntaFormData({
      title:        apt.title,
      date:         toLocalDateString(apt.date),
      time:         d.toTimeString().slice(0, 5),
      duration:     '1hr',
      sede:         apt.sede || '',
      participants: apt.participants || [],
      recurrence:   'none',
      repeatUntil:  '',
      notes:        apt.description || '',
      videoLink:    apt.videoLink || '',
      color:        apt.color || '',
    });
    setJuntaDialogOpen(true);
  };

  const handleSubmitJunta = async (e) => {
    e.preventDefault();
    const recurrence = juntaFormData.recurrence !== 'none' ? juntaFormData.recurrence : null;
    if (recurrence && !juntaFormData.repeatUntil) {
      toast.error('Indica la fecha "Repetir hasta" para la recurrencia');
      return;
    }
    const dateTime = new Date(`${juntaFormData.date}T${juntaFormData.time}`);
    const durOpt   = JUNTA_DURATION_OPTIONS.find(d => d.value === juntaFormData.duration);
    const endDT    = new Date(dateTime.getTime() + (durOpt?.mins || 60) * 60000);
    const payload  = {
      title:          juntaFormData.title,
      start_datetime: dateTime.toISOString(),
      end_datetime:   endDT.toISOString(),
      event_kind:     'junta',
      sede:           juntaFormData.sede || null,
      participants:   juntaFormData.participants,
      recurrence,
      repeat_until:   recurrence ? juntaFormData.repeatUntil : undefined,
      description:    juntaFormData.notes,
      videoLink:      juntaFormData.videoLink || null,
      color:          juntaFormData.color || null,
    };
    try {
      if (editingJunta) {
        await axios.put(`${API}/calendar/events/${editingJunta.id}`, payload, authHeaders);
        toast.success('Junta actualizada correctamente');
      } else {
        const res  = await axios.post(`${API}/calendar/events`, payload, authHeaders);
        const evts = Array.isArray(res.data) ? res.data : (res.data?.events ?? [res.data]);
        toast.success(evts.length > 1 ? `${evts.length} juntas creadas con recurrencia` : 'Junta creada correctamente');
      }
      loadEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar la junta');
      return;
    }
    setJuntaDialogOpen(false);
    resetJuntaForm();
  };

  const resetJuntaForm = () => {
    setEditingJunta(null);
    setJuntaFormData({ title: '', date: '', time: '10:00', duration: '1hr', sede: '', participants: [], recurrence: 'none', repeatUntil: '', notes: '', videoLink: '', color: '' });
    setJuntaSearch('');
  };

  const handleTimeChange = (newTime) => {
    const [oldH, oldM] = (formData.time || '09:00').split(':').map(Number);
    const [endH, endM] = (formData.endTime || '10:00').split(':').map(Number);
    const durationMins = (endH * 60 + endM) - (oldH * 60 + oldM);
    const [newH, newM] = newTime.split(':').map(Number);
    const newEndTotal = newH * 60 + newM + (durationMins > 0 ? durationMins : 30);
    const newEndH = Math.floor(newEndTotal / 60) % 24;
    const newEndM = newEndTotal % 60;
    const newEndTime = `${String(newEndH).padStart(2, '0')}:${String(newEndM).padStart(2, '0')}`;
    setFormData({ ...formData, time: newTime, endTime: newEndTime });
  };

  const handleJuntaTimeChange = (newTime) => {
    setJuntaFormData(p => ({ ...p, time: newTime }));
  };

  const getWeekDays = () => {
    const start = new Date(currentDate);
    start.setDate(start.getDate() - start.getDay());
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return d;
    });
  };

  const isToday = (date) =>
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear();

  // Source label for synthetic events
  const sourceLabel = { birthday: '🎂', renewal: '🔄', policy_start: '✅', google: 'G' };

  const isAllDayEvent = (apt) =>
    apt.allDay ||
    apt.source === 'birthday' ||
    apt.type?.includes('birthday') ||
    apt.title?.toLowerCase().includes('cumpleaños') ||
    new Date(apt.date).getHours() === 0;

  const handleAppointmentClick = (apt) => {
    handleViewAppointment(apt);
  };

  // Scroll semana a hora actual al cambiar a vista semana
  useEffect(() => {
    const scrollEl = weekScrollRef.current;
    if (!scrollEl || viewMode !== 'week') return;
    const now = new Date();
    const scrollHour = Math.max(START_HOUR, now.getHours() - 1);
    scrollEl.scrollTop = (scrollHour - START_HOUR) * HOUR_HEIGHT;
  }, [viewMode]);

  // Alerta 30 min antes de cita/junta
  const alertedEvents = useRef(new Set());
  useEffect(() => {
    const checkUpcoming = async () => {
      const now = new Date();
      const in30 = new Date(now.getTime() + 30 * 60 * 1000);
      const upcoming = appointments.filter(apt => {
        if (!['cita', 'junta'].includes(apt.kind)) return false;
        if (apt.user_id !== user?.id) return false;
        const start = new Date(apt.date);
        return start > now && start <= in30;
      });
      for (const apt of upcoming) {
        if (alertedEvents.current.has(apt.id)) continue;
        alertedEvents.current.add(apt.id);
        const minsLeft = Math.round((new Date(apt.date) - now) / 60000);
        const msg = `Tu ${apt.kind} "${apt.title}" comienza en ${minsLeft} minutos`;
        toast(`📅 ${apt.title}`, { description: msg, duration: 10000 });
        try {
          await axios.post(`${API}/notifications`, {
            type: 'calendar_reminder',
            title: `Recordatorio: ${apt.title}`,
            message: msg,
            action_data: { event_id: apt.id, action_url: '/calendar' },
          }, authHeaders);
          refreshNotifications();
        } catch (e) {
          console.error('Error guardando notificación de calendario:', e);
        }
        try {
          await axios.post(`${API}/notifications/email-reminder`, {
            title: apt.title,
            message: msg,
            event_kind: apt.kind,
            start_time: apt.start_datetime || apt.date,
          }, authHeaders);
        } catch (e) {
          console.error('Error enviando correo de recordatorio:', e);
        }
      }
    };
    const interval = setInterval(checkUpcoming, 60000);
    checkUpcoming();
    return () => clearInterval(interval);
  }, [appointments, user]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-6" data-testid="calendar-page">

      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <CalendarIcon className="h-8 w-8 text-[#1a365d]" />
            Calendario
          </h1>
          <p className="text-muted-foreground">Gestiona tus citas y reuniones</p>
        </div>

        <div className="flex flex-wrap gap-2 items-center">
          {/* Sede filter */}
          {false && availableSedes.length > 0 && (
            <Select value={filterSede} onValueChange={setFilterSede}>
              <SelectTrigger className="w-36 h-9 text-xs">
                <SelectValue placeholder="Sede" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las sedes</SelectItem>
                {availableSedes.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          )}

          {/* Agent filter (admin + asistente) */}
          {(isAdmin || isAsistente) && (availableUsers.length > 0 || calendarUsers.length > 1) && (
            <Select
              value={filterAgent}
              onValueChange={v => {
                setFilterAgent(v);
                if (isAsistente) {
                  setActiveUsers(v === (user?.id || 'me') ? [user.id] : [v]);
                }
              }}
            >
              <SelectTrigger className="w-44 h-9 text-xs">
                <SelectValue placeholder="Filtrar agente" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={user?.id || 'me'}>Mi Calendario</SelectItem>
                {!isAsistente && <SelectItem value="all">Todos</SelectItem>}
                {[...(isAsistente ? calendarUsers : availableUsers)]
                  .filter(u => u.id !== user?.id)
                  .sort((a, b) =>
                    (a.full_name || a.name || '').localeCompare(b.full_name || b.name || '', 'es')
                  )
                  .map(u => (
                    <SelectItem key={u.id} value={u.id}>
                      {isAsistente ? u.name : (u.full_name || u.name || u.email)}
                    </SelectItem>
                  ))
                }
              </SelectContent>
            </Select>
          )}

          {/* View mode */}
          <div className="flex rounded-lg border">
            <Button variant={viewMode === 'month' ? 'default' : 'ghost'} size="sm" onClick={() => setViewMode('month')} className={viewMode === 'month' ? 'bg-[#1a365d]' : ''}>Mes</Button>
            <Button variant={viewMode === 'week'  ? 'default' : 'ghost'} size="sm" onClick={() => setViewMode('week')}  className={viewMode === 'week'  ? 'bg-[#1a365d]' : ''}>Semana</Button>
          </div>

          {/* Color preferences popover */}
          <div className="relative">
            <Button
              variant="outline"
              size="sm"
              className="h-9 px-2"
              title="Preferencias de color"
              onClick={() => setPrefsOpen(p => !p)}
            >
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: calendarPrefs.color_cita }} />
                <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: calendarPrefs.color_junta }} />
              </span>
            </Button>
            {prefsOpen && (
              <div className="absolute right-0 top-10 z-50 bg-white dark:bg-gray-900 border rounded-lg shadow-lg p-4 w-56 space-y-3">
                <p className="text-sm font-medium">Colores de mi calendario</p>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={calendarPrefs.color_cita}
                    onChange={e => setCalendarPrefs(p => ({ ...p, color_cita: e.target.value }))}
                    className="w-8 h-8 rounded cursor-pointer border p-0.5"
                  />
                  <label className="text-sm">Mis citas</label>
                </div>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={calendarPrefs.color_junta}
                    onChange={e => setCalendarPrefs(p => ({ ...p, color_junta: e.target.value }))}
                    className="w-8 h-8 rounded cursor-pointer border p-0.5"
                  />
                  <label className="text-sm">Mis juntas</label>
                </div>
                <button
                  onClick={saveCalendarPrefs}
                  disabled={savingPrefs}
                  className="w-full text-sm bg-[#1a365d] text-white rounded py-1.5 disabled:opacity-50"
                >
                  {savingPrefs ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            )}
          </div>

          {/* Google sync */}
          {gcalConnected && (
            <Button variant="outline" size="sm" onClick={syncGoogleCalendar} disabled={gcalSyncing}>
              <RefreshCw className={`h-4 w-4 mr-1 ${gcalSyncing ? 'animate-spin' : ''}`} />
              {gcalSyncing ? 'Sincronizando...' : 'Sincronizar'}
            </Button>
          )}

          {/* Nueva Junta (admin + asistente) */}
          {canCreateJunta && (
            <Dialog open={juntaDialogOpen} onOpenChange={(o) => { setJuntaDialogOpen(o); if (!o) resetJuntaForm(); }}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-[#8b5cf6] text-[#8b5cf6] hover:bg-[#8b5cf6]/10">
                  <Users className="h-4 w-4 mr-2" />
                  Nueva Junta
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>{editingJunta ? 'Editar Junta' : 'Nueva Junta Grupal'}</DialogTitle>
                  <DialogDescription>{editingJunta ? 'Modifica los datos de la junta' : 'Programa una junta con el equipo'}</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmitJunta} className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
                  <div className="space-y-2">
                    <Label>Título *</Label>
                    <Input value={juntaFormData.title} onChange={e => setJuntaFormData(p => ({ ...p, title: e.target.value }))} placeholder="Reunión semanal de equipo..." required />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Fecha *</Label>
                      <Input type="date" value={juntaFormData.date} onChange={e => setJuntaFormData(p => ({ ...p, date: e.target.value }))} required />
                    </div>
                    <div className="space-y-2">
                      <Label>Hora *</Label>
                      <datalist id="time-junta">
                        {TIME_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </datalist>
                      <Input
                        list="time-junta"
                        value={juntaFormData.time}
                        onChange={e => handleJuntaTimeChange(e.target.value)}
                        placeholder="09:00"
                        required
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Duración</Label>
                      <Select value={juntaFormData.duration} onValueChange={v => setJuntaFormData(p => ({ ...p, duration: v }))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{JUNTA_DURATION_OPTIONS.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-1"><MapPin className="h-3 w-3" /> Sede / Lugar</Label>
                      <Input value={juntaFormData.sede} onChange={e => setJuntaFormData(p => ({ ...p, sede: e.target.value }))} placeholder="Sala de juntas..." />
                    </div>
                  </div>

                  {availableUsers.length > 0 && (
                    <div className="space-y-2">
                      <Label className="flex items-center gap-1"><Users className="h-3 w-3" /> Participantes</Label>
                      <input
                        type="text"
                        placeholder="Buscar participante..."
                        value={juntaSearch}
                        onChange={e => setJuntaSearch(e.target.value)}
                        className="w-full px-2 py-1 text-sm border rounded bg-background"
                      />
                      <div className="flex gap-2 flex-wrap">
                        {juntaAseguradoraLines.map(line => {
                          const aseguradoraName = LINE_ID_TO_ASEGURADORA[line.id];
                          const label = juntaAseguradoraLines.length === 1
                            ? 'Seleccionar todos'
                            : `Seleccionar todos ${line.name}`;
                          const idsForLine = usersForAseguradora(aseguradoraName);
                          const allSelected = idsForLine.length > 0
                            && idsForLine.every(id => juntaFormData.participants.includes(id));
                          return (
                            <button
                              key={line.id}
                              type="button"
                              onClick={() => setJuntaFormData(p => ({
                                ...p,
                                participants: allSelected
                                  ? p.participants.filter(id => !idsForLine.includes(id))
                                  : [...new Set([...p.participants, ...idsForLine])],
                              }))}
                              className={`text-xs px-2 py-1 rounded border flex items-center gap-1 ${
                                allSelected
                                  ? `${line.color} text-white border-transparent`
                                  : 'border-input bg-background hover:bg-muted'
                              }`}
                            >
                              {allSelected && <Check className="h-3 w-3" />}
                              {label}
                            </button>
                          );
                        })}
                        {meetingStaff.length > 0 && (
                          <button
                            type="button"
                            onClick={() => {
                              const validIds = meetingStaff.filter(id => id !== user?.id && availableUsers.some(u => u.id === id));
                              setJuntaFormData(p => ({
                                ...p,
                                participants: [...new Set([...p.participants, ...validIds])],
                              }));
                            }}
                            className="text-xs px-2 py-1 rounded border border-input bg-background hover:bg-muted"
                          >
                            Seleccionar Staff
                          </button>
                        )}
                      </div>
                      <div className="max-h-36 overflow-y-auto border rounded-lg p-2 space-y-1 bg-muted/20">
                        {[...availableUsers]
                          .sort((a, b) => (a.full_name || a.name || a.email || '').localeCompare(b.full_name || b.name || b.email || '', 'es', { sensitivity: 'base' }))
                          .filter(u => (u.full_name || u.name || u.email || '').toLowerCase().includes(juntaSearch.toLowerCase()))
                          .map(u => (
                          <label key={u.id} className="flex items-center gap-2 cursor-pointer text-sm p-1 hover:bg-muted rounded">
                            <input
                              type="checkbox"
                              checked={juntaFormData.participants.includes(u.id)}
                              onChange={e => setJuntaFormData(p => ({
                                ...p,
                                participants: e.target.checked
                                  ? [...p.participants, u.id]
                                  : p.participants.filter(id => id !== u.id),
                              }))}
                              className="rounded"
                            />
                            <span>{u.full_name || u.name || u.email}</span>
                            {u.role && <span className="text-xs text-muted-foreground">({u.role})</span>}
                          </label>
                        ))}
                      </div>
                      {juntaFormData.participants.length > 0 && (
                        <p className="text-xs text-muted-foreground">{juntaFormData.participants.length} participante(s) seleccionado(s)</p>
                      )}
                    </div>
                  )}

                  {!editingJunta && (
                    <div className="space-y-2">
                      <Label>Repetir</Label>
                      <Select value={juntaFormData.recurrence} onValueChange={v => setJuntaFormData(p => ({ ...p, recurrence: v, repeatUntil: '' }))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{RECURRENCE_OPTIONS.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  )}

                  {!editingJunta && juntaFormData.recurrence !== 'none' && (
                    <div className="space-y-2">
                      <Label>Repetir hasta <span className="text-red-500">*</span></Label>
                      <Input type="date" value={juntaFormData.repeatUntil} onChange={e => setJuntaFormData(p => ({ ...p, repeatUntil: e.target.value }))} min={juntaFormData.date || undefined} required />
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label className="flex items-center gap-1"><Video className="h-3 w-3" /> Link de videollamada</Label>
                    <Input
                      type="url"
                      value={juntaFormData.videoLink}
                      onChange={e => setJuntaFormData(p => ({ ...p, videoLink: e.target.value }))}
                      placeholder="https://meet.google.com/..."
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Notas / Agenda</Label>
                    <Textarea value={juntaFormData.notes} onChange={e => setJuntaFormData(p => ({ ...p, notes: e.target.value }))} placeholder="Temas a tratar, agenda..." rows={3} />
                  </div>

                  <div className="space-y-2">
                    <Label>Color de la junta</Label>
                    <div className="flex items-center gap-2 h-10">
                      <input
                        type="color"
                        value={juntaFormData.color || calendarPrefs.color_junta}
                        onChange={e => setJuntaFormData(p => ({ ...p, color: e.target.value }))}
                        className="w-10 h-10 rounded cursor-pointer border p-0.5"
                        title="Color de la junta"
                      />
                      {juntaFormData.color && (
                        <button type="button" onClick={() => setJuntaFormData(p => ({ ...p, color: '' }))} className="text-xs text-muted-foreground underline">
                          Por defecto
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setJuntaDialogOpen(false)}>Cancelar</Button>
                    <Button type="submit" style={{ backgroundColor: '#8b5cf6' }} className="text-white hover:opacity-90">
                      {editingJunta ? 'Guardar Cambios' : 'Crear Junta'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}

          {/* Nueva Cita */}
          {canCreate && (
            <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="bg-[#1a365d] hover:bg-[#1a365d]/90" data-testid="new-appointment-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Nueva Cita
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>{editingAppointment ? 'Editar Cita' : 'Nueva Cita'}</DialogTitle>
                  <DialogDescription>{editingAppointment ? 'Modifica los datos de la cita' : 'Crea una nueva cita o reunión'}</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto">
                  {isAsistente && !editingAppointment && calendarUsers.length > 1 && (
                    <div className="space-y-2">
                      <Label>Crear para</Label>
                      <Select value={forUserId || user?.id} onValueChange={setForUserId}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {calendarUsers.map(cu => (
                            <SelectItem key={cu.id} value={cu.id}>
                              <span className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: cu.color }} />
                                {cu.name}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="title">Título *</Label>
                    <Input id="title" value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} placeholder="Reunión con cliente..." required data-testid="appointment-title" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="date">Fecha *</Label>
                      <Input id="date" type="date" value={formData.date} onChange={e => setFormData({ ...formData, date: e.target.value })} required data-testid="appointment-date" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="type">Asociar a</Label>
                      <Select value={formData.type} onValueChange={v => setFormData({ ...formData, type: v })}>
                        <SelectTrigger><SelectValue placeholder="Tipo" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="client">Cliente</SelectItem>
                          <SelectItem value="lead">Lead</SelectItem>
                          <SelectItem value="agent">Agente</SelectItem>
                          <SelectItem value="other">Otro</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Tipo de cita</Label>
                      <Select value={formData.cita_subtype || 'none'} onValueChange={v => setFormData({ ...formData, cita_subtype: v === 'none' ? '' : v })}>
                        <SelectTrigger><SelectValue placeholder="Sin especificar" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Sin especificar</SelectItem>
                          <SelectItem value="inicial">Cita Inicial</SelectItem>
                          <SelectItem value="cierre">Cita de Cierre</SelectItem>
                          <SelectItem value="seguimiento">Seguimiento</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Color del evento</Label>
                      <div className="flex items-center gap-2 h-10">
                        <input
                          type="color"
                          value={formData.color || calendarPrefs.color_cita}
                          onChange={e => setFormData({ ...formData, color: e.target.value })}
                          className="w-10 h-10 rounded cursor-pointer border p-0.5"
                          title="Color del evento"
                        />
                        {formData.color && (
                          <button type="button" onClick={() => setFormData({ ...formData, color: '' })} className="text-xs text-muted-foreground underline">
                            Por defecto
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="time">Hora Inicio *</Label>
                        {startTimeMode === 'select'
                          ? <button type="button" onClick={() => setStartTimeMode('manual')} style={{fontSize:'11px',color:'#6366f1',background:'none',border:'none',cursor:'pointer',padding:0}}>✏️ Hora exacta</button>
                          : <button type="button" onClick={() => setStartTimeMode('select')} style={{fontSize:'11px',color:'#6366f1',background:'none',border:'none',cursor:'pointer',padding:0}}>← Ver opciones</button>
                        }
                      </div>
                      {startTimeMode === 'select' ? (
                        <Select value={formData.time} onValueChange={handleTimeChange}>
                          <SelectTrigger id="time" className="h-10">
                            <SelectValue>{formData.time || '09:00'}</SelectValue>
                          </SelectTrigger>
                          <SelectContent className="max-h-[280px] overflow-y-auto">
                            {TIME_OPTIONS_FULL.map(t => (
                              <SelectItem key={t.value} value={t.value}>
                                <div className="font-medium">{t.label24}</div>
                                <div className="text-xs text-muted-foreground">{t.label12}</div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input id="time" type="time" className="h-10" value={formData.time || '09:00'} onChange={e => handleTimeChange(e.target.value)} />
                      )}
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="endTime">Hora Fin</Label>
                        {endTimeMode === 'select'
                          ? <button type="button" onClick={() => setEndTimeMode('manual')} style={{fontSize:'11px',color:'#6366f1',background:'none',border:'none',cursor:'pointer',padding:0}}>✏️ Hora exacta</button>
                          : <button type="button" onClick={() => setEndTimeMode('select')} style={{fontSize:'11px',color:'#6366f1',background:'none',border:'none',cursor:'pointer',padding:0}}>← Ver opciones</button>
                        }
                      </div>
                      {endTimeMode === 'select' ? (
                        <Select value={formData.endTime} onValueChange={v => setFormData({ ...formData, endTime: v })}>
                          <SelectTrigger id="endTime" className="h-10">
                            <SelectValue>{formData.endTime || '10:00'}</SelectValue>
                          </SelectTrigger>
                          <SelectContent className="max-h-[280px] overflow-y-auto">
                            {TIME_OPTIONS_FULL.map(t => (
                              <SelectItem key={t.value} value={t.value}>
                                <div className="font-medium">{t.label24}</div>
                                <div className="text-xs text-muted-foreground">{t.label12}</div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input id="endTime" type="time" className="h-10" value={formData.endTime || '10:00'} onChange={e => setFormData({ ...formData, endTime: e.target.value })} />
                      )}
                    </div>
                  </div>

                  {/* Invitados */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2"><UserPlus className="h-4 w-4" /> Agregar Invitado</Label>
                    <div className="space-y-2 p-3 border rounded-lg bg-muted/30">
                      <div className="grid grid-cols-2 gap-2">
                        <Input value={newGuest.name}  onChange={e => setNewGuest({ ...newGuest, name:  e.target.value })} placeholder="Nombre del invitado" />
                        <Input type="email" value={newGuest.email} onChange={e => setNewGuest({ ...newGuest, email: e.target.value })} placeholder="Correo electrónico" />
                      </div>
                      <Button type="button" variant="outline" size="sm" onClick={addGuest} className="w-full">
                        <Plus className="h-4 w-4 mr-2" />Agregar Invitado
                      </Button>
                    </div>
                    {formData.guests.length > 0 && (
                      <div className="space-y-1 mt-2">
                        <Label className="text-sm text-muted-foreground">Invitados ({formData.guests.length})</Label>
                        {formData.guests.map((guest, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 bg-muted rounded-md">
                            <div className="flex items-center gap-2">
                              <User className="h-4 w-4 text-muted-foreground" />
                              <div className="text-sm">
                                {guest.name && <span className="font-medium">{guest.name}</span>}
                                {guest.name && guest.email && <span className="text-muted-foreground"> • </span>}
                                {guest.email && <span className="text-muted-foreground">{guest.email}</span>}
                              </div>
                            </div>
                            <Button type="button" variant="ghost" size="icon" className="h-6 w-6 text-red-500 hover:text-red-700" onClick={() => removeGuest(idx)}>
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Video link */}
                  <div className="space-y-2">
                    <Label htmlFor="videoLink" className="flex items-center gap-2"><Video className="h-4 w-4" /> Link de videollamada</Label>
                    <Input id="videoLink" type="url" value={formData.videoLink} onChange={e => setFormData({ ...formData, videoLink: e.target.value })} placeholder="https://meet.google.com/..." />
                    <p className="text-xs text-muted-foreground">Puedes agregar links de Meet, Zoom, Teams u otra plataforma</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">Descripción</Label>
                    <Textarea id="description" value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} placeholder="Describe los temas a tratar..." rows={3} />
                  </div>

                  {!editingAppointment && (
                    <div className="space-y-2">
                      <Label>Repetir</Label>
                      <Select value={formData.recurrence || 'none'} onValueChange={v => setFormData({ ...formData, recurrence: v, repeatUntil: '' })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{RECURRENCE_OPTIONS.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  )}

                  {!editingAppointment && formData.recurrence && formData.recurrence !== 'none' && (
                    <div className="space-y-2">
                      <Label htmlFor="repeatUntil">Repetir hasta <span className="text-red-500">*</span></Label>
                      <Input id="repeatUntil" type="date" value={formData.repeatUntil} onChange={e => setFormData({ ...formData, repeatUntil: e.target.value })} min={formData.date || undefined} required />
                    </div>
                  )}

                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                    <Button type="submit" className="bg-[#1a365d] hover:bg-[#1a365d]/90">{editingAppointment ? 'Guardar Cambios' : 'Crear Cita'}</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* ── Toggles de calendario por usuario (solo asistente) ── */}
      {isAsistente && calendarUsers.length > 1 && calendarUsers.length <= 6 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {calendarUsers.map(cu => (
            <button
              key={cu.id}
              type="button"
              onClick={() => setActiveUsers(prev =>
                prev.includes(cu.id) ? prev.filter(id => id !== cu.id) : [...prev, cu.id]
              )}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${activeUsers.includes(cu.id) ? 'text-white border-transparent' : 'bg-white text-muted-foreground border-border'}`}
              style={activeUsers.includes(cu.id) ? { backgroundColor: cu.color, borderColor: cu.color } : {}}
            >
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: cu.color }} />
              {cu.name}
            </button>
          ))}
        </div>
      )}

      {/* ── Calendar grid ── */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}><ChevronLeft className="h-5 w-5" /></Button>
            <CardTitle className="text-xl">
              {viewMode === 'week' ? (() => {
                const wd = getWeekDays();
                if (wd[0].getMonth() === wd[6].getMonth()) return `${wd[0].getDate()} - ${wd[6].getDate()} ${MONTHS[wd[0].getMonth()]} ${wd[0].getFullYear()}`;
                return `${wd[0].getDate()} ${MONTHS[wd[0].getMonth()]} - ${wd[6].getDate()} ${MONTHS[wd[6].getMonth()]} ${wd[0].getFullYear()}`;
              })() : `${MONTHS[currentDate.getMonth()]} ${currentDate.getFullYear()}`}
            </CardTitle>
            <Button variant="ghost" size="icon" onClick={() => navigate(1)}><ChevronRight className="h-5 w-5" /></Button>
          </div>
        </CardHeader>
        <CardContent>
          {viewMode === 'month' ? (
            <div className="grid grid-cols-7 gap-1">
              {WEEKDAYS.map(day => (
                <div key={day} className="text-center text-sm font-medium text-muted-foreground py-2">{day}</div>
              ))}
              {days.map((dayObj, index) => {
                const dayAppointments = getAppointmentsForDate(dayObj.date);
                const isTodayDate     = isToday(dayObj.date);
                return (
                  <div
                    key={index}
                    onClick={() => canCreate && dayObj.isCurrentMonth && handleDateClick(dayObj.date)}
                    className={`min-h-[100px] p-1 border rounded-lg transition-colors ${dayObj.isCurrentMonth ? 'bg-background' : 'bg-muted/30'} ${isTodayDate ? 'border-[#1a365d] border-2' : 'border-border'} ${canCreate && dayObj.isCurrentMonth ? 'cursor-pointer hover:bg-muted/50' : ''}`}
                    data-testid={`calendar-day-${dayObj.date.getDate()}`}
                  >
                    <div className={`text-sm font-medium mb-1 ${isTodayDate ? 'text-[#1a365d]' : ''} ${!dayObj.isCurrentMonth ? 'text-muted-foreground' : ''}`}>
                      {dayObj.date.getDate()}
                    </div>
                    <div className="space-y-0.5">
                      {dayAppointments.slice(0, 3).map(apt => {
                        const { bgStyle, bgClass } = getEventStyle(apt);
                        return (
                          <div
                            key={apt.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewAppointment(apt);
                            }}
                            onMouseEnter={(e) => { e.stopPropagation(); setHoveredEvent(apt); setTooltipPosition({ x: e.clientX, y: e.clientY }); }}
                            onMouseLeave={() => setHoveredEvent(null)}
                            className={`p-1 rounded truncate cursor-pointer ${bgClass}`}
                            style={{ ...bgStyle, fontSize: '13px', fontWeight: '500', color: getTextColor(bgStyle?.backgroundColor) }}
                            title={apt.title}
                          >
                            {apt.source && sourceLabel[apt.source] ? <span className="mr-0.5">{sourceLabel[apt.source]}</span> : null}
                            {!apt.allDay && `${new Date(apt.date).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })} `}{apt.title}
                            {getUserLabel(apt) && (
                              <span className="block text-[9px] opacity-75 leading-tight truncate">
                                {getUserLabel(apt)}
                              </span>
                            )}
                          </div>
                        );
                      })}
                      {dayAppointments.length > 3 && (
                        <div className="text-xs text-muted-foreground text-center">+{dayAppointments.length - 3} más</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            /* Vista semanal — timeline */
            <div className="space-y-0">
              {/* Header días */}
              <div className="flex border-b border-border">
                <div className="w-16 flex-shrink-0" />
                <div className="flex-1 grid grid-cols-7">
                  {getWeekDays().map((date, i) => (
                    <div key={i} className={`text-center p-2 ${isToday(date) ? 'bg-[#1a365d] text-white rounded-t-lg' : ''}`}>
                      <div className="text-xs">{WEEKDAYS[date.getDay()]}</div>
                      <div className="text-lg font-bold">{date.getDate()}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Franja todo el día */}
              <div className="flex border-b border-border">
                <div className="w-16 flex-shrink-0 text-xs text-muted-foreground p-1 pt-2">Todo día</div>
                <div className="flex-1 grid grid-cols-7">
                  {getWeekDays().map((date, i) => {
                    const allDay = getAppointmentsForDate(date).filter(apt => isAllDayEvent(apt));
                    return (
                      <div key={i} className="border-l border-border/30 min-h-[24px] p-0.5">
                        {allDay.map(apt => {
                          const { bgStyle, bgClass } = getEventStyle(apt);
                          return (
                            <div
                              key={apt.id}
                              className={`rounded px-1 truncate mb-0.5 cursor-pointer ${bgClass}`}
                              style={{ ...bgStyle, fontSize: '13px', fontWeight: '500', color: getTextColor(bgStyle?.backgroundColor) }}
                              onClick={() => handleAppointmentClick(apt)}
                            >
                              {apt.title}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Timeline scrollable */}
              <div ref={weekScrollRef} className="flex overflow-y-auto max-h-[calc(100vh-220px)]">
                {/* Columna horas */}
                <div className="w-16 flex-shrink-0">
                  {Array.from({ length: END_HOUR - START_HOUR }, (_, i) => START_HOUR + i).map(hour => (
                    <div
                      key={hour}
                      style={{ height: HOUR_HEIGHT }}
                      className="flex items-start justify-end pr-2 text-xs text-muted-foreground border-t border-border/30"
                    >
                      {hour === 0 ? '12 AM' : hour === 12 ? '12 PM' : hour < 12 ? `${hour} AM` : `${hour - 12} PM`}
                    </div>
                  ))}
                </div>

                {/* Grid 7 días */}
                <div className="flex-1 grid grid-cols-7">
                  {getWeekDays().map((date, dayIndex) => {
                    const dayApts = getAppointmentsForDate(date).filter(apt => !isAllDayEvent(apt));
                    const isNow = isToday(date);
                    const nowHour = new Date().getHours();
                    const nowTop = isNow && nowHour >= START_HOUR && nowHour < END_HOUR
                      ? getEventTop(new Date().toISOString())
                      : null;

                    return (
                      <div
                        key={dayIndex}
                        className="relative border-l border-border/30"
                        style={{ height: (END_HOUR - START_HOUR) * HOUR_HEIGHT }}
                      >
                        {/* Líneas de hora */}
                        {Array.from({ length: END_HOUR - START_HOUR }, (_, i) => (
                          <div
                            key={i}
                            className="absolute w-full border-t border-border/20"
                            style={{ top: i * HOUR_HEIGHT }}
                          />
                        ))}

                        {/* Línea "ahora" */}
                        {nowTop !== null && (
                          <div
                            className="absolute w-full flex items-center z-10"
                            style={{ top: nowTop }}
                          >
                            <div className="w-2 h-2 rounded-full bg-red-500 -ml-1" />
                            <div className="flex-1 h-[2px] bg-red-500" />
                          </div>
                        )}

                        {/* Eventos */}
                        {(() => {
                          // Detectar solapamientos y asignar columnas
                          const sorted = [...dayApts].sort((a, b) => new Date(a.date) - new Date(b.date));
                          const cols = new Array(sorted.length).fill(0);
                          const ends = [];
                          for (let i = 0; i < sorted.length; i++) {
                            const start = new Date(sorted[i].date).getTime();
                            const end = sorted[i].endTime ? new Date(sorted[i].endTime).getTime() : start + 30 * 60 * 1000;
                            let col = 0;
                            while (ends[col] !== undefined && ends[col] > start) col++;
                            cols[i] = col;
                            ends[col] = end;
                          }
                          // Calcular totalCols por grupo solapado
                          const maxColForGroup = (idx) => {
                            const start = new Date(sorted[idx].date).getTime();
                            const end = sorted[idx].endTime ? new Date(sorted[idx].endTime).getTime() : start + 30 * 60 * 1000;
                            let max = 0;
                            for (let j = 0; j < sorted.length; j++) {
                              const s2 = new Date(sorted[j].date).getTime();
                              const e2 = sorted[j].endTime ? new Date(sorted[j].endTime).getTime() : s2 + 30 * 60 * 1000;
                              if (s2 < end && e2 > start) max = Math.max(max, cols[j]);
                            }
                            return max + 1;
                          };
                          return sorted.map((apt, i) => {
                            const top    = getEventTop(apt.date);
                            const height = getEventHeight(apt.date, apt.endTime);
                            const { bgStyle, bgClass } = getEventStyle(apt);
                            const colIdx   = cols[i];
                            const totalCols = maxColForGroup(i);
                            const leftPct  = (colIdx / totalCols) * 100;
                            const widthPct = (1 / totalCols) * 100;
                            return (
                              <div
                                key={apt.id}
                                className={`absolute rounded px-1 py-0.5 cursor-pointer overflow-hidden z-20 ${bgClass}`}
                                style={{
                                  top, height, minHeight: 20,
                                  left:  `calc(${leftPct}% + 2px)`,
                                  width: `calc(${widthPct}% - 4px)`,
                                  fontSize: '13px', fontWeight: '500',
                                  ...bgStyle,
                                  color: getTextColor(bgStyle?.backgroundColor),
                                }}
                                onClick={() => handleAppointmentClick(apt)}
                              >
                                <div className="font-medium truncate">{apt.title}</div>
                                {height > 30 && (
                                  <div className="opacity-80 truncate text-[10px]">
                                    {new Date(apt.date).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                                  </div>
                                )}
                                {height > 45 && getUserLabel(apt) && (
                                  <div className="opacity-75 truncate text-[9px]">
                                    {getUserLabel(apt)}
                                  </div>
                                )}
                              </div>
                            );
                          });
                        })()}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* ── Color legend / type filters ── */}
          <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-4 pt-3 border-t">
            {COLOR_LEGEND.map(item => (
              <label key={item.key} className="flex items-center gap-1.5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={visibleTypes[item.key]}
                  onChange={() => setVisibleTypes(prev => ({ ...prev, [item.key]: !prev[item.key] }))}
                  className="sr-only"
                />
                <span
                  className="w-3 h-3 rounded-sm flex-shrink-0 border-2 transition-opacity"
                  style={{ backgroundColor: visibleTypes[item.key] ? item.color : 'transparent', borderColor: item.color }}
                />
                <span className={`text-xs transition-opacity ${visibleTypes[item.key] ? 'text-foreground' : 'text-muted-foreground line-through'}`}>
                  {item.label}
                </span>
              </label>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* ── Próximas Citas (cita / junta) ── */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Clock className="h-5 w-5" /> Próximas Citas</CardTitle>
          <CardDescription>Citas y juntas programadas para los próximos días</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {(() => {
              const citaTypes = ['cita', 'junta'];
              const items = [...appointments, ...gcalEvents]
                .filter(apt => {
                  if (new Date(apt.date) < today) return false;
                  if (!visibleTypes[getEventTypeKey(apt)]) return false;
                  if (!citaTypes.includes(getEventTypeKey(apt))) return false;
                  if (isAsistente) {
                    if (activeUsers.length > 0 && apt.user_id &&
                        !activeUsers.includes(apt.user_id) &&
                        !(apt.participants || []).includes(user?.id))
                      return false;
                  } else {
                    if (filterAgent !== 'all' &&
                        apt.user_id !== filterAgent &&
                        !(apt.participants || []).includes(filterAgent))
                      return false;
                  }
                  return true;
                })
                .sort((a, b) => new Date(a.date) - new Date(b.date))
                .slice(0, 5);
              if (items.length === 0) return (
                <div className="text-center py-8 text-muted-foreground">
                  <CalendarIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>Sin citas próximas.</p>
                </div>
              );
              return items.map(apt => {
                const typeConfig = appointmentTypes[apt.type] || appointmentTypes.other;
                const TypeIcon   = typeConfig.icon;
                const aptDate    = new Date(apt.date);
                const { bgStyle, bgClass } = getEventStyle(apt);
                return (
                  <HoverCard key={apt.id} openDelay={200} closeDelay={100}>
                    <HoverCardTrigger asChild>
                      <div className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 transition-colors cursor-pointer" onClick={() => handleViewAppointment(apt)}>
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg text-white ${bgClass}`} style={bgStyle}>
                            {apt.source === 'google' ? <span className="text-xs font-bold">G</span> : <TypeIcon className="h-4 w-4" />}
                          </div>
                          <div>
                            <p className="font-medium">{apt.title}</p>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <CalendarIcon className="h-3 w-3" />
                              {formatDate(apt.date)}
                              {apt.allDay ? (
                                <span className="ml-2 text-xs italic">Todo el día</span>
                              ) : (
                                <>
                                  <Clock className="h-3 w-3 ml-2" />
                                  {aptDate.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                                </>
                              )}
                              {apt.videoLink && <Video className="h-3 w-3 ml-2 text-blue-500" />}
                              {apt.kind === 'junta' && <Badge variant="outline" className="text-[#8b5cf6] border-[#8b5cf6] text-xs py-0">Junta</Badge>}
                              {apt.source === 'google' && <Badge variant="outline" className="text-[#ea4335] border-[#ea4335] text-xs py-0">Google</Badge>}
                              {apt.cita_subtype && (
                                <Badge variant="outline" className="text-xs py-0">
                                  {apt.cita_subtype === 'inicial' ? 'Inicial' : apt.cita_subtype === 'cierre' ? 'Cierre' : 'Seguimiento'}
                                </Badge>
                              )}
                            </div>
                            {getUserLabel(apt) && (
                              <span className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                                <User className="h-3 w-3" />
                                {getUserLabel(apt)}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {apt.videoLink && (
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-500 hover:text-blue-700" onClick={e => copyVideoLink(apt.videoLink, e)} title="Copiar link">
                              <Copy className="h-4 w-4" />
                            </Button>
                          )}
                          <Badge className={bgClass} style={bgStyle}>{getEventLabel(apt)}</Badge>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={e => e.stopPropagation()}>
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleViewAppointment(apt)}>
                                <CalendarIcon className="h-4 w-4 mr-2" />Ver Detalles
                              </DropdownMenuItem>
                              {!apt.isReadonly && (
                                <DropdownMenuItem onClick={() => handleEdit(apt)}>
                                  <Edit className="h-4 w-4 mr-2" />Editar
                                </DropdownMenuItem>
                              )}
                              {apt.videoLink && (
                                <DropdownMenuItem onClick={() => window.open(apt.videoLink, '_blank')}>
                                  <ExternalLink className="h-4 w-4 mr-2" />Abrir Videollamada
                                </DropdownMenuItem>
                              )}
                              {!apt.isReadonly && (isAdmin || user?.role === 'master' || apt.user_id === user?.id) && (
                                <DropdownMenuItem onClick={() => handleDelete(apt.id)} className="text-red-600">
                                  <Trash2 className="h-4 w-4 mr-2" />Eliminar
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    </HoverCardTrigger>
                    <HoverCardContent className="w-80" align="start">
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center justify-between">
                          <h4 className="font-semibold">{apt.title}</h4>
                          <Badge className={bgClass} style={bgStyle}>{getEventLabel(apt)}</Badge>
                        </div>
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <CalendarIcon className="h-4 w-4" />
                          <span>{apt.allDay ? formatDate(apt.date) : `${formatDate(apt.date)} a las ${aptDate.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}`}</span>
                        </div>
                        {apt.endTime && !apt.allDay && (
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Clock className="h-4 w-4" /><span>Termina: {apt.endTime}</span>
                          </div>
                        )}
                        {apt.sede && (
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <MapPin className="h-4 w-4" /><span>{apt.sede}</span>
                          </div>
                        )}
                        {apt.guests?.length > 0 && (
                          <div className="flex items-start gap-2 text-muted-foreground">
                            <Users className="h-4 w-4 mt-0.5" />
                            <div>
                              <span className="font-medium">Invitados: </span>
                              {apt.guests.map((g, i) => <div key={i} className="text-xs">{g.name}{g.email && ` (${g.email})`}</div>)}
                            </div>
                          </div>
                        )}
                        {apt.videoLink && (
                          <div className="flex items-center gap-2">
                            <Video className="h-4 w-4 text-blue-500" />
                            <a href={apt.videoLink} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline truncate flex-1" onClick={e => e.stopPropagation()}>
                              {apt.videoLink.length > 30 ? apt.videoLink.substring(0, 30) + '...' : apt.videoLink}
                            </a>
                          </div>
                        )}
                        {(apt.description || apt.notes) && (
                          <div className="pt-1 border-t">
                            <p className="text-muted-foreground text-xs">{apt.description || apt.notes}</p>
                          </div>
                        )}
                      </div>
                    </HoverCardContent>
                  </HoverCard>
                );
              });
            })()}
          </div>
        </CardContent>
      </Card>

      {/* ── Aniversarios y Eventos ── */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><CalendarIcon className="h-5 w-5" /> Aniversarios y Eventos</CardTitle>
          <CardDescription>Aniversarios de póliza, cumpleaños, renovaciones e inicios</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {(() => {
              const eventTypes = ['anniversary', 'birthday', 'renewal', 'policy_start'];
              const items = [...appointments, ...gcalEvents]
                .filter(apt => new Date(apt.date) >= today && visibleTypes[getEventTypeKey(apt)] && eventTypes.includes(getEventTypeKey(apt)))
                .sort((a, b) => new Date(a.date) - new Date(b.date))
                .slice(0, 5);
              if (items.length === 0) return (
                <div className="text-center py-8 text-muted-foreground">
                  <CalendarIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>Sin eventos próximos.</p>
                </div>
              );
              return items.map(apt => {
                const typeConfig = appointmentTypes[apt.type] || appointmentTypes.other;
                const TypeIcon   = typeConfig.icon;
                const aptDate    = new Date(apt.date);
                const { bgStyle, bgClass } = getEventStyle(apt);
                return (
                  <HoverCard key={apt.id} openDelay={200} closeDelay={100}>
                    <HoverCardTrigger asChild>
                      <div className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 transition-colors cursor-pointer" onClick={() => handleViewAppointment(apt)}>
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg text-white ${bgClass}`} style={bgStyle}>
                            <TypeIcon className="h-4 w-4" />
                          </div>
                          <div>
                            <p className="font-medium">{apt.title}</p>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <CalendarIcon className="h-3 w-3" />
                              {formatDate(apt.date)}
                              {apt.allDay
                                ? <span className="ml-2 text-xs italic">Todo el día</span>
                                : <><Clock className="h-3 w-3 ml-2" />{aptDate.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}</>
                              }
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={bgClass} style={bgStyle}>{getEventLabel(apt)}</Badge>
                        </div>
                      </div>
                    </HoverCardTrigger>
                    <HoverCardContent className="w-80" align="start">
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center justify-between">
                          <h4 className="font-semibold">{apt.title}</h4>
                          <Badge className={bgClass} style={bgStyle}>{getEventLabel(apt)}</Badge>
                        </div>
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <CalendarIcon className="h-4 w-4" />
                          <span>{apt.allDay ? formatDate(apt.date) : `${formatDate(apt.date)} a las ${aptDate.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}`}</span>
                        </div>
                        {apt.clientName && (
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <User className="h-4 w-4" /><span>{apt.clientName}</span>
                          </div>
                        )}
                        {apt.policyNumber && (
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <span className="text-xs">Póliza: {apt.policyNumber}</span>
                          </div>
                        )}
                        {apt.years && (
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <span className="text-xs font-medium">{apt.years} año{apt.years !== 1 ? 's' : ''} de vigencia</span>
                          </div>
                        )}
                        {(apt.description || apt.notes) && (
                          <div className="pt-1 border-t">
                            <p className="text-muted-foreground text-xs">{apt.description || apt.notes}</p>
                          </div>
                        )}
                      </div>
                    </HoverCardContent>
                  </HoverCard>
                );
              });
            })()}
          </div>
        </CardContent>
      </Card>

      {/* ── Hover tooltip ── */}
      {hoveredEvent && !dialogOpen && !viewDialogOpen && !juntaDialogOpen && (
        <div
          className="fixed z-40 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-xl max-w-sm pointer-events-none"
          style={{ left: tooltipPosition.x + 12, top: tooltipPosition.y - 10 }}
        >
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-sm">{hoveredEvent.title}</h3>
            {hoveredEvent.kind === 'junta' && <Badge className="text-xs ml-2" style={{ backgroundColor: '#8b5cf6' }}>Junta</Badge>}
            {hoveredEvent.source === 'birthday' && <Badge className="text-xs ml-2" style={{ backgroundColor: '#f59e0b' }}>Cumpleaños</Badge>}
            {hoveredEvent.source === 'renewal'  && <Badge className="text-xs ml-2" style={{ backgroundColor: '#3b82f6' }}>Renovación</Badge>}
            {hoveredEvent.source === 'policy_start' && <Badge className="text-xs ml-2" style={{ backgroundColor: '#10b981' }}>Inicio póliza</Badge>}
            {hoveredEvent.source === 'anniversary'  && <Badge className="text-xs ml-2" style={{ backgroundColor: '#F5CF27', color: '#1a1a1a' }}>Aniversario</Badge>}
            {hoveredEvent.source === 'google'   && <Badge className="text-xs ml-2" style={{ backgroundColor: '#ea4335' }}>Google</Badge>}
          </div>
          <p className="text-xs text-gray-600 dark:text-gray-300 flex items-center gap-1">
            <CalendarIcon className="h-3 w-3" />
            {formatDate(hoveredEvent.date)} {new Date(hoveredEvent.date).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
            {hoveredEvent.endTime && ` - ${hoveredEvent.endTime}`}
          </p>
          {hoveredEvent.sede && <p className="text-xs text-gray-500 mt-1 flex items-center gap-1"><MapPin className="h-3 w-3" />{hoveredEvent.sede}</p>}
          {hoveredEvent.guests?.length > 0 && (
            <p className="text-xs text-gray-600 dark:text-gray-300 mt-1">
              Invitados: {hoveredEvent.guests.map(g => g.name || g.email).filter(Boolean).join(', ')}
            </p>
          )}
          {hoveredEvent.videoLink && (
            <p className="text-xs text-blue-500 mt-1 truncate">
              {hoveredEvent.videoLink.length > 35 ? hoveredEvent.videoLink.substring(0, 35) + '...' : hoveredEvent.videoLink}
            </p>
          )}
          {(hoveredEvent.description || hoveredEvent.notes) && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 border-t pt-1">
              {hoveredEvent.description || hoveredEvent.notes}
            </p>
          )}
          {hoveredEvent.isReadonly && (
            <p className="text-xs text-muted-foreground mt-1 italic">Solo lectura</p>
          )}
        </div>
      )}

      {/* ── Delete recurrence dialog ── */}
      <Dialog open={deleteDialogOpen} onOpenChange={(o) => { setDeleteDialogOpen(o); if (!o) setDeleteTarget(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Eliminar evento recurrente</DialogTitle>
            <DialogDescription>
              Este evento es parte de una serie de{' '}
              <strong>{deleteTarget?.recurrence_group_size ?? '?'}</strong> repeticiones.
              ¿Qué deseas eliminar?
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2 pt-2">
            <Button variant="outline" onClick={handleDeleteSingle} className="justify-start">
              <Trash2 className="h-4 w-4 mr-2 text-orange-500" />
              Eliminar solo este evento
            </Button>
            <Button variant="outline" onClick={handleDeleteSeries} className="justify-start text-red-600 border-red-200 hover:bg-red-50">
              <Trash2 className="h-4 w-4 mr-2" />
              Eliminar toda la serie ({deleteTarget?.recurrence_group_size ?? '?'} eventos)
            </Button>
            <Button variant="ghost" onClick={() => { setDeleteDialogOpen(false); setDeleteTarget(null); }}>
              Cancelar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── View dialog ── */}
      <Dialog open={viewDialogOpen} onOpenChange={(open) => { setViewDialogOpen(open); if (!open) setJuntaParticipantNames([]); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarIcon className="h-5 w-5" />
              {viewingAppointment?.kind === 'junta' ? 'Detalles de la Junta' : 'Detalles de la Cita'}
            </DialogTitle>
          </DialogHeader>
          {viewingAppointment && (
            <div className="space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h3 className="text-lg font-semibold">{viewingAppointment.title}</h3>
                <div className="flex items-center gap-1.5 flex-wrap">
                  {viewingAppointment.kind === 'junta'        && <Badge style={{ backgroundColor: '#8b5cf6' }} className="text-white">Junta</Badge>}
                  {viewingAppointment.source === 'birthday'   && <Badge style={{ backgroundColor: '#f59e0b' }} className="text-white">Cumpleaños</Badge>}
                  {viewingAppointment.source === 'renewal'    && <Badge style={{ backgroundColor: '#dc2626' }} className="text-white">Renovación</Badge>}
                  {viewingAppointment.source === 'policy_start' && <Badge style={{ backgroundColor: '#10b981' }} className="text-white">Inicio póliza</Badge>}
                  {viewingAppointment.source === 'anniversary' && <Badge style={{ backgroundColor: '#F5CF27', color: '#1a1a1a' }}>Aniversario</Badge>}
                  {viewingAppointment.source === 'google'     && <Badge style={{ backgroundColor: '#ea4335' }} className="text-white">Google Calendar</Badge>}
                  {(!viewingAppointment.kind || viewingAppointment.kind === 'cita') && !viewingAppointment.source && (
                    <Badge className={(appointmentTypes[viewingAppointment.type] || appointmentTypes.other).color}>
                      {(appointmentTypes[viewingAppointment.type] || appointmentTypes.other).label}
                    </Badge>
                  )}
                  {viewingAppointment.cita_subtype && (
                    <Badge variant="outline">
                      {viewingAppointment.cita_subtype === 'inicial' ? 'Inicial' : viewingAppointment.cita_subtype === 'cierre' ? 'Cierre' : 'Seguimiento'}
                    </Badge>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">Fecha</Label>
                  <p className="flex items-center gap-2"><CalendarIcon className="h-4 w-4 text-muted-foreground" />{formatDate(viewingAppointment.date)}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">Hora</Label>
                  <p className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    {viewingAppointment.allDay
                      ? <span className="italic text-muted-foreground">Todo el día</span>
                      : <>
                          {new Date(viewingAppointment.date).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                          {viewingAppointment.endTime && ` - ${new Date(viewingAppointment.endTime).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}`}
                        </>
                    }
                  </p>
                </div>
              </div>

              {getUserLabel(viewingAppointment) && (
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs flex items-center gap-1"><User className="h-3 w-3" /> Agente</Label>
                  <p className="text-sm">{getUserLabel(viewingAppointment)}</p>
                </div>
              )}

              {viewingAppointment.kind === 'junta' && juntaParticipantNames.length > 0 && (
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs flex items-center gap-1"><Users className="h-3 w-3" /> Participantes</Label>
                  <ul className="space-y-1">
                    {juntaParticipantNames.map((p) => (
                      <li key={p.id} className="text-sm flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#8b5cf6] flex-shrink-0" />
                        {p.name}
                        {p.isHost && <span className="text-xs border border-[#8b5cf6] text-[#8b5cf6] rounded px-1 py-0.5 leading-none">Anfitrión</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {viewingAppointment.sede && (
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs flex items-center gap-1"><MapPin className="h-3 w-3" /> Sede</Label>
                  <p className="text-sm">{viewingAppointment.sede}</p>
                </div>
              )}

              {viewingAppointment.guests?.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-xs flex items-center gap-1"><Users className="h-3 w-3" /> Invitados ({viewingAppointment.guests.length})</Label>
                  <div className="space-y-1">
                    {viewingAppointment.guests.map((guest, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2 bg-muted rounded-md text-sm">
                        <User className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{guest.name || 'Sin nombre'}</span>
                        {guest.email && <><span className="text-muted-foreground">•</span><span className="text-muted-foreground">{guest.email}</span></>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {viewingAppointment.videoLink && (
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-xs flex items-center gap-1"><Video className="h-3 w-3" /> Link de Videollamada</Label>
                  <div className="flex items-center gap-2 p-3 bg-blue-50 dark:bg-blue-950 rounded-lg border border-blue-200 dark:border-blue-800">
                    <Video className="h-5 w-5 text-blue-500 flex-shrink-0" />
                    <a href={viewingAppointment.videoLink} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate flex-1 text-sm">{viewingAppointment.videoLink}</a>
                    <Button variant="outline" size="sm" onClick={() => copyVideoLink(viewingAppointment.videoLink)} className="flex-shrink-0"><Copy className="h-4 w-4 mr-1" />Copiar</Button>
                    <Button variant="default" size="sm" onClick={() => window.open(viewingAppointment.videoLink, '_blank')} className="flex-shrink-0 bg-blue-600 hover:bg-blue-700"><ExternalLink className="h-4 w-4 mr-1" />Abrir</Button>
                  </div>
                </div>
              )}

              {(viewingAppointment.description || viewingAppointment.notes) && (
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-xs">Descripción</Label>
                  <p className="text-sm p-3 bg-muted rounded-lg">{viewingAppointment.description || viewingAppointment.notes}</p>
                </div>
              )}

              <div className="flex justify-between gap-2 pt-4 border-t">
                <div>
                  {!viewingAppointment.isReadonly && (isAdmin || user?.role === 'master' || viewingAppointment.user_id === user?.id) && (
                    <Button
                      variant="destructive"
                      onClick={async () => {
                        if (!window.confirm('¿Eliminar este evento? Esta acción no se puede deshacer.')) return;
                        try {
                          await axios.delete(`${API}/calendar/events/${viewingAppointment.id}`, authHeaders);
                          setViewDialogOpen(false);
                          loadEvents();
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Error al eliminar el evento');
                        }
                      }}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />Eliminar
                    </Button>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setViewDialogOpen(false)}>Cerrar</Button>
                  {!viewingAppointment.isReadonly && (
                    viewingAppointment.kind === 'junta' ? canCreateJunta : (isAdmin || user?.role === 'master' || viewingAppointment.user_id === user?.id)
                  ) && (
                    <Button variant="outline" onClick={() => {
                      setViewDialogOpen(false);
                      if (viewingAppointment.kind === 'junta') {
                        handleEditJunta(viewingAppointment);
                      } else {
                        handleEdit(viewingAppointment);
                      }
                    }}>
                      <Edit className="h-4 w-4 mr-2" />Editar
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
